import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useI18n } from "../lib/i18n";
import { useAuth, API } from "../lib/auth";
import StatusBadge from "../components/StatusBadge";
import AdminAnalytics from "../components/AdminAnalytics";
import AdminStaffPanel, { AggregatorPanel } from "../components/AdminStaffPanel";
import EmployerReport from "../components/EmployerReport";
import JobEditDialog from "../components/JobEditDialog";
import AdminCreateEmployerDialog from "../components/AdminCreateEmployerDialog";
import AdminBulkJobsDialog from "../components/AdminBulkJobsDialog";
import MayaChatHistory from "../components/MayaChatHistory";
import AdminCandidateDetailDialog from "../components/AdminCandidateDetailDialog";
import AdminSponsors from "../components/AdminSponsors";
import AdminEmployerApprovals from "../components/AdminEmployerApprovals";
import { Users, Briefcase, FileText, CreditCard, Trash2, Plus, ShieldCheck, Newspaper, BarChart3, Download, Power, Pencil, BarChart, Upload, UserPlus, Mail, CheckCircle2, MessageCircle, Megaphone, UserCheck } from "lucide-react";

export default function AdminDashboard() {
  const { t } = useI18n();
  const { authHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [apps, setApps] = useState([]);
  const [news, setNews] = useState([]);
  const [topupOpen, setTopupOpen] = useState(false);
  const [topupTarget, setTopupTarget] = useState(null);
  const [topupAmount, setTopupAmount] = useState(10);
  const [newsForm, setNewsForm] = useState({ title: "", content: "" });
  const [reportEmployerId, setReportEmployerId] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [editJob, setEditJob] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [createEmpOpen, setCreateEmpOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const [candDetailId, setCandDetailId] = useState(null);
  const [candDetailOpen, setCandDetailOpen] = useState(false);

  const openCandidate = (id) => { setCandDetailId(id); setCandDetailOpen(true); };

  const load = async () => {
    const [s, c, e, j, a, n, q] = await Promise.all([
      axios.get(`${API}/admin/stats`, { headers: authHeader }),
      axios.get(`${API}/admin/users?role=candidate`, { headers: authHeader }),
      axios.get(`${API}/admin/users?role=employer`, { headers: authHeader }),
      axios.get(`${API}/admin/jobs`, { headers: authHeader }),
      axios.get(`${API}/admin/applications`, { headers: authHeader }),
      axios.get(`${API}/news`),
      axios.get(`${API}/admin/pricing-inquiries`, { headers: authHeader }).catch(() => ({ data: { items: [] } })),
    ]);
    setStats(s.data); setCandidates(c.data); setEmployers(e.data); setJobs(j.data); setApps(a.data); setNews(n.data);
    setInquiries(q.data?.items || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const deleteUser = async (id) => {
    await axios.delete(`${API}/admin/users/${id}`, { headers: authHeader });
    toast.success("Supprimé"); load();
  };
  const deleteJob = async (id) => {
    await axios.delete(`${API}/admin/jobs/${id}`, { headers: authHeader });
    toast.success("Supprimé"); load();
  };
  const assignPlan = async (employer_id, plan) => {
    await axios.post(`${API}/admin/plan/assign`, { employer_id, plan }, { headers: authHeader });
    toast.success("Plan mis à jour"); load();
  };
  const topup = async () => {
    if (!topupTarget) return;
    await axios.post(`${API}/admin/credits/topup`, { employer_id: topupTarget.id, amount: Number(topupAmount) }, { headers: authHeader });
    toast.success("Crédits ajoutés"); setTopupOpen(false); load();
  };
  const createNews = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/admin/news`, newsForm, { headers: authHeader });
    setNewsForm({ title: "", content: "" });
    toast.success("Article publié"); load();
  };

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)]" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center gap-3 mb-8">
          <ShieldCheck className="h-7 w-7 text-[#1A8FE3]" />
          <div>
            <span className="text-xs uppercase tracking-[0.25em] text-[#1A8FE3] font-semibold">Motravay</span>
            <h1 className="font-display text-3xl font-bold text-slate-900">{t("admin.dashboard")}</h1>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <StatCard icon={Users} label="Candidats" value={stats?.candidates ?? "—"} />
          <StatCard icon={Briefcase} label="Recruteurs" value={stats?.employers ?? "—"} />
          <StatCard icon={FileText} label="Offres" value={stats?.jobs ?? "—"} />
          <StatCard icon={Briefcase} label="Actives" value={stats?.active_jobs ?? "—"} />
          <StatCard icon={Users} label="Candidatures" value={stats?.applications ?? "—"} />
        </div>

        <Tabs defaultValue="analytics">
          <TabsList className="bg-white border border-slate-200 flex-wrap h-auto">
            <TabsTrigger value="analytics" data-testid="ad-tab-analytics"><BarChart3 className="h-4 w-4 mr-1" />Analytics</TabsTrigger>
            <TabsTrigger value="employers" data-testid="ad-tab-employers">Employers</TabsTrigger>
            <TabsTrigger value="candidates" data-testid="ad-tab-candidates">Candidates</TabsTrigger>
            <TabsTrigger value="jobs" data-testid="ad-tab-jobs">Jobs</TabsTrigger>
            <TabsTrigger value="apps" data-testid="ad-tab-apps">Applications</TabsTrigger>
            <TabsTrigger value="news" data-testid="ad-tab-news">News</TabsTrigger>
            <TabsTrigger value="quotes" data-testid="ad-tab-quotes" className="relative">
              <Mail className="h-4 w-4 mr-1" />Quotes
              {inquiries.filter(i => i.status === "new").length > 0 && (
                <span data-testid="ad-quotes-badge" className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-rose-500 text-white text-[10px] font-bold px-1">
                  {inquiries.filter(i => i.status === "new").length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="chats" data-testid="ad-tab-chats"><MessageCircle className="h-4 w-4 mr-1" />Maya chats</TabsTrigger>
            <TabsTrigger value="staff" data-testid="ad-tab-staff"><ShieldCheck className="h-4 w-4 mr-1" />Staff</TabsTrigger>
            <TabsTrigger value="aggregator" data-testid="ad-tab-aggregator"><Download className="h-4 w-4 mr-1" />Aggregator</TabsTrigger>
            <TabsTrigger value="sponsors" data-testid="ad-tab-sponsors"><Megaphone className="h-4 w-4 mr-1" />Sponsored Ads</TabsTrigger>
            <TabsTrigger value="approvals" data-testid="ad-tab-approvals"><UserCheck className="h-4 w-4 mr-1" />Approvals</TabsTrigger>
          </TabsList>

          <TabsContent value="analytics" className="mt-6">
            <AdminAnalytics />
          </TabsContent>

          <TabsContent value="employers" className="mt-6">
            <div className="mb-3 flex justify-end">
              <Button data-testid="admin-open-create-employer" onClick={() => setCreateEmpOpen(true)} className="bg-[#1A8FE3] hover:bg-[#157AC0] text-white rounded-full">
                <UserPlus className="h-4 w-4 mr-1" /> Create employer
              </Button>
            </div>
            <Card className="overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-700 uppercase text-xs tracking-wider">
                  <tr><th className="px-4 py-3 text-left">Entreprise</th><th className="px-4 py-3 text-left">Email</th><th className="px-4 py-3 text-left">Plan</th><th className="px-4 py-3 text-left">Crédits</th><th className="px-4 py-3 text-left">Actions</th></tr>
                </thead>
                <tbody>
                  {employers.map(e => (
                    <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium">
                        <button
                          data-testid={`employer-name-${e.id}`}
                          onClick={() => { setReportEmployerId(e.id); setReportOpen(true); }}
                          className="text-left text-[#1A8FE3] hover:text-[#157AC0] hover:underline inline-flex items-center gap-1"
                        >
                          <BarChart className="h-3.5 w-3.5" />{e.company_name}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{e.email}</td>
                      <td className="px-4 py-3">
                        <Select value={e.plan || "starter"} onValueChange={(v) => assignPlan(e.id, v)}>
                          <SelectTrigger data-testid={`plan-${e.id}`} className="h-9 w-32"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="starter">Starter</SelectItem>
                            <SelectItem value="pro">Pro</SelectItem>
                            <SelectItem value="enterprise">Enterprise</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3 text-slate-600 font-mono">{e.credits ?? 0}</td>
                      <td className="px-4 py-3 flex gap-2">
                        <Button size="sm" variant="outline" data-testid={`topup-${e.id}`} onClick={() => { setTopupTarget(e); setTopupOpen(true); }} className="rounded-full">
                          <CreditCard className="h-3 w-3 mr-1" />{t("admin.topup")}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteUser(e.id)} className="text-rose-600 hover:bg-rose-50"><Trash2 className="h-3 w-3" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </TabsContent>

          <TabsContent value="candidates" className="mt-6">
            <Card className="overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-700 uppercase text-xs tracking-wider"><tr><th className="px-4 py-3 text-left">Nom</th><th className="px-4 py-3 text-left">Job title</th><th className="px-4 py-3 text-left">Email</th><th className="px-4 py-3 text-left">Téléphone</th><th className="px-4 py-3 text-left">Inscrit le</th><th className="px-4 py-3"></th></tr></thead>
                <tbody>
                  {candidates.map(c => (
                    <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium">
                        <button
                          type="button"
                          data-testid={`ad-candidate-${c.id}`}
                          onClick={() => openCandidate(c.id)}
                          className="text-left text-[#1A8FE3] hover:text-[#157AC0] hover:underline font-medium"
                        >
                          {c.name}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-slate-600" data-testid={`ad-candidate-title-${c.id}`}>{c.title || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{c.email}</td>
                      <td className="px-4 py-3 text-slate-600">{c.phone || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{new Date(c.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3"><Button size="sm" variant="ghost" onClick={() => deleteUser(c.id)} className="text-rose-600"><Trash2 className="h-3 w-3" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </TabsContent>

          <TabsContent value="jobs" className="mt-6">
            <div className="mb-3 flex justify-end">
              <Button data-testid="admin-open-bulk-jobs" onClick={() => setBulkOpen(true)} className="bg-[#1A8FE3] hover:bg-[#157AC0] text-white rounded-full">
                <Upload className="h-4 w-4 mr-1" /> Bulk upload (CSV)
              </Button>
            </div>
            <Card className="overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-700 uppercase text-xs tracking-wider"><tr><th className="px-4 py-3 text-left">Title</th><th className="px-4 py-3 text-left">Company</th><th className="px-4 py-3 text-left">Location</th><th className="px-4 py-3 text-left">Status</th><th className="px-4 py-3 text-left">Views</th><th className="px-4 py-3 text-left">Apps</th><th className="px-4 py-3"></th></tr></thead>
                <tbody>
                  {jobs.map(j => (
                    <tr key={j.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium">{j.title}</td>
                      <td className="px-4 py-3 text-slate-600">{j.company_name}</td>
                      <td className="px-4 py-3 text-slate-600">{j.location}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full ${j.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-700'}`}>{j.status}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{j.views || 0}</td>
                      <td className="px-4 py-3 text-slate-600">{j.applications_count || 0}</td>
                      <td className="px-4 py-3 flex gap-1">
                        <Button size="sm" variant="ghost" data-testid={`job-edit-${j.id}`} onClick={() => { setEditJob(j); setEditOpen(true); }} className="text-slate-700"><Pencil className="h-3 w-3" /></Button>
                        <Button size="sm" variant="ghost" data-testid={`job-toggle-${j.id}`} onClick={async () => {
                          await axios.patch(`${API}/admin/jobs/${j.id}/status`, { status: j.status === "active" ? "paused" : "active" }, { headers: authHeader });
                          load();
                        }} className="text-slate-700"><Power className="h-3 w-3" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteJob(j.id)} className="text-rose-600"><Trash2 className="h-3 w-3" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </TabsContent>

          <TabsContent value="apps" className="mt-6">
            <Card className="overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-700 uppercase text-xs tracking-wider"><tr><th className="px-4 py-3 text-left">Candidat</th><th className="px-4 py-3 text-left">Offre</th><th className="px-4 py-3 text-left">Date</th><th className="px-4 py-3 text-left">Statut</th></tr></thead>
                <tbody>
                  {apps.map(a => (
                    <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium">
                        {a.candidate_id ? (
                          <button
                            type="button"
                            data-testid={`ad-app-candidate-${a.id}`}
                            onClick={() => openCandidate(a.candidate_id)}
                            className="text-left text-[#1A8FE3] hover:text-[#157AC0] hover:underline font-medium"
                          >
                            {a.candidate_name}
                          </button>
                        ) : (
                          a.candidate_name
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{a.job_title}</td>
                      <td className="px-4 py-3 text-slate-600">{new Date(a.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </TabsContent>

          <TabsContent value="news" className="mt-6 space-y-6">
            <Card className="p-6">
              <h3 className="font-display text-lg font-semibold mb-4 inline-flex items-center gap-2"><Newspaper className="h-4 w-4" />Nouvel article</h3>
              <form onSubmit={createNews} className="space-y-3">
                <div><Label>Titre</Label><Input data-testid="news-title" required value={newsForm.title} onChange={(e) => setNewsForm({ ...newsForm, title: e.target.value })} /></div>
                <div><Label>Contenu</Label><Textarea data-testid="news-content" required rows={4} value={newsForm.content} onChange={(e) => setNewsForm({ ...newsForm, content: e.target.value })} /></div>
                <Button type="submit" data-testid="news-submit" className="bg-[#1A8FE3] hover:bg-[#157AC0] text-white rounded-full"><Plus className="h-3 w-3 mr-1" />Publier</Button>
              </form>
            </Card>
            <div className="space-y-3">
              {news.map(n => (
                <Card key={n.id} className="p-4 flex justify-between items-start">
                  <div>
                    <p className="text-xs text-slate-500">{new Date(n.created_at).toLocaleDateString()}</p>
                    <h4 className="font-semibold text-slate-900">{n.title}</h4>
                    <p className="text-sm text-slate-600 mt-1">{n.content}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={async () => { await axios.delete(`${API}/admin/news/${n.id}`, { headers: authHeader }); load(); }} className="text-rose-600"><Trash2 className="h-3 w-3" /></Button>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="staff" className="mt-6">
            <AdminStaffPanel />
          </TabsContent>

          <TabsContent value="chats" className="mt-6">
            <MayaChatHistory />
          </TabsContent>

          <TabsContent value="quotes" className="mt-6">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-lg font-semibold inline-flex items-center gap-2"><Mail className="h-4 w-4" />{t("admin.quoteRequests")}</h3>
                <span className="text-xs text-slate-500">{inquiries.length} {t("admin.totalLabel")}</span>
              </div>
              {inquiries.length === 0 ? (
                <p className="text-sm text-slate-500 py-8 text-center">{t("admin.noQuotes")}</p>
              ) : (
                <div className="space-y-3" data-testid="ad-quotes-list">
                  {inquiries.map((q) => (
                    <Card key={q.id} data-testid={`ad-quote-${q.id}`} className={`p-4 border ${q.status === "new" ? "border-rose-200 bg-rose-50/30" : "border-slate-200"}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h4 className="font-semibold text-slate-900">{q.company_name}</h4>
                            {q.status === "new" ? (
                              <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-rose-500 text-white">{t("admin.statusNew")}</span>
                            ) : (
                              <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{t("admin.statusHandled")}</span>
                            )}
                            <span className="text-xs text-slate-500">{new Date(q.created_at).toLocaleString()}</span>
                          </div>
                          <p className="text-sm text-slate-700 mt-1">
                            <span className="font-medium">{q.contact_name}</span> · <a className="text-[#1A8FE3] hover:underline" href={`mailto:${q.email}`}>{q.email}</a>
                            {q.phone ? <> · <a className="text-[#1A8FE3] hover:underline" href={`tel:${q.phone}`}>{q.phone}</a></> : null}
                          </p>
                          {q.vacancies ? <p className="text-xs text-slate-500 mt-1">{t("admin.vacanciesLabel")}: {q.vacancies}</p> : null}
                          <p className="text-sm text-slate-700 mt-2 whitespace-pre-line">{q.needs}</p>
                        </div>
                        <div className="flex flex-col gap-2 shrink-0">
                          {q.status === "new" && (
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`ad-quote-mark-${q.id}`}
                              onClick={async () => {
                                await axios.patch(`${API}/admin/pricing-inquiries/${q.id}`, { status: "handled" }, { headers: authHeader });
                                toast.success(t("admin.markedHandled"));
                                load();
                              }}
                            >
                              <CheckCircle2 className="h-3 w-3 mr-1" />{t("admin.markHandled")}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-rose-600 hover:text-rose-700"
                            data-testid={`ad-quote-delete-${q.id}`}
                            onClick={async () => {
                              await axios.delete(`${API}/admin/pricing-inquiries/${q.id}`, { headers: authHeader });
                              toast.success(t("admin.deleted"));
                              load();
                            }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="aggregator" className="mt-6">
            <AggregatorPanel />
          </TabsContent>

          <TabsContent value="sponsors" className="mt-6">
            <AdminSponsors />
          </TabsContent>

          <TabsContent value="approvals" className="mt-6">
            <AdminEmployerApprovals />
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={topupOpen} onOpenChange={setTopupOpen}>
        <DialogContent data-testid="topup-dialog">
          <DialogHeader><DialogTitle>Recharger crédits — {topupTarget?.company_name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Montant</Label><Input data-testid="topup-amount" type="number" value={topupAmount} onChange={(e) => setTopupAmount(e.target.value)} /></div>
            <Button onClick={topup} data-testid="topup-confirm" className="w-full bg-[#1A8FE3] hover:bg-[#157AC0] rounded-full text-white">Confirmer</Button>
          </div>
        </DialogContent>
      </Dialog>

      <EmployerReport employerId={reportEmployerId} open={reportOpen} onOpenChange={(o) => { setReportOpen(o); if (!o) setReportEmployerId(null); }} />
      <JobEditDialog job={editJob} open={editOpen} onOpenChange={(o) => { setEditOpen(o); if (!o) setEditJob(null); }} onSaved={() => load()} />
      <AdminCreateEmployerDialog open={createEmpOpen} onOpenChange={setCreateEmpOpen} onCreated={() => load()} />
      <AdminBulkJobsDialog open={bulkOpen} onOpenChange={setBulkOpen} employers={employers} onUploaded={() => load()} />
      <AdminCandidateDetailDialog candidateId={candDetailId} open={candDetailOpen} onOpenChange={(o) => { setCandDetailOpen(o); if (!o) setCandDetailId(null); }} />
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-widest text-slate-500 font-medium">{label}</span>
        <Icon className="h-5 w-5 text-[#1A8FE3]" />
      </div>
      <div className="font-display text-3xl font-bold text-slate-900 mt-2">{value}</div>
    </Card>
  );
}
